import { useGetLanguage } from '@/hooks';
import { OpinionsApi } from '@/modules/opinions';
import { useStore } from '@/store';

import { useInfiniteQuery } from '@tanstack/react-query';
import { ReactNode, createContext, useContext } from 'react';

interface OpinionsContextType {
  data: any;
  isLoading: boolean;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
}

export const OpinionsContext = createContext<OpinionsContextType | null>(null);

export const useOpinions = () => {
  const context = useContext(OpinionsContext);
  if (!context) {
    throw new Error('useOpinions must be used within an OpinionsProvider');
  }
  return context;
};

export const OpinionsProvider = ({
  profileId,
  me,
  children,
}: {
  profileId: string;
  me: boolean;
  children: ReactNode;
}) => {
  const {
    accountStore: { accountData },
  } = useStore();
  const lang = useGetLanguage();
  const LIMIT = 3;

  const query = useInfiniteQuery({
    queryKey: ['profileOpinions', profileId, me, lang],
    queryFn: async ({ pageParam = 0 }) => {
      return await OpinionsApi.getOpinions({
        lang,
        limit: LIMIT,
        skip: pageParam * LIMIT,
        filters: { createdBy: me ? accountData._id : profileId },
      });
    },
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length < LIMIT ? undefined : allPages.length,
    initialPageParam: 0,
    enabled: profileId !== 'me',
  });

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    query;

  return (
    <OpinionsContext.Provider
      value={{
        data,
        isLoading,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
      }}
    >
      {children}
    </OpinionsContext.Provider>
  );
};
